import { useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { isBrandedHost, activeHostBrand } from '@/components/HostBranding';

// Public surfaces that must work on a branded host WITHOUT org membership:
// the client portal (read-only token link) and the invite-claim flow.
const PUBLIC_PREFIXES = ['/p/', '/accept-invite'];

// The only logged-in app surfaces a branded host serves. Anything else (our
// marketing landing, /features, /pricing, /blog, even /cctv-design-tool) bounces
// to /dashboard. Exact-or-subpath match so '/cctv-design' (the designer) is in
// but '/cctv-design-tool' (a marketing page) is NOT.
const APP_PREFIXES = ['/dashboard', '/organization', '/settings', '/checkout-success', '/cctv-design', '/api-docs'];
function isAppRoute(p: string): boolean {
  return APP_PREFIXES.some((a) => p === a || p.startsWith(a + '/'));
}

/**
 * Branded-host access isolation. On a customer's domain (planner.securitas.se /
 * acme.todaidesign.net) only the host OWNER and their ACTIVE team members may use
 * the app — anyone else (other companies, free users) is locked out. The public
 * client portal stays open. A no-op on the main domain (todaidesign.net).
 * Backed by the SECURITY DEFINER RPC my_host_access().
 */
export default function BrandedHostGuard({ children }: { children: React.ReactNode }) {
  const host = isBrandedHost();
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('dashboard');
  const [access, setAccess] = useState<'checking' | 'allowed' | 'denied'>('checking');

  const isPublic = PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  const isAuthRoute = location.pathname.startsWith('/auth') || location.pathname.startsWith('/login');

  useEffect(() => {
    if (!host || isPublic || loading || !user) return;
    let cancelled = false;
    setAccess('checking');
    supabase.rpc('my_host_access' as never, { p_host: host } as never).then(({ data, error }) => {
      if (!cancelled) setAccess(!error && data === true ? 'allowed' : 'denied');
    });
    return () => { cancelled = true; };
  }, [host, isPublic, user, loading]);

  // Main domain → no guard. Public portal / accept-invite → always open.
  if (!host || isPublic) return <>{children}</>;

  if (loading) return <Centered>{t('common:loading', { defaultValue: 'Loading…' })}</Centered>;

  // Not signed in → a branded host shows ONLY its login gate. Entering the host
  // (root included) lands straight on /auth — the customer's branded login —
  // never our marketing landing, in a single hop.
  if (!user) {
    if (isAuthRoute) return <>{children}</>;
    return <Navigate to="/auth" replace />;
  }

  // Signed in: a branded host serves ONLY the app workspace. Any non-app route
  // (root, /features, /pricing, /blog, /cctv-design-tool, …) → /dashboard.
  if (!isAppRoute(location.pathname) && !isAuthRoute) return <Navigate to="/dashboard" replace />;

  if (access === 'checking') return <Centered>{t('common:loading', { defaultValue: 'Loading…' })}</Centered>;

  if (access === 'denied') {
    const brand = activeHostBrand;
    const color = brand?.primaryColor || 'var(--color-brand-blue)';
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full text-center border rounded-lg p-8" style={{ borderColor: 'var(--color-gray-border, #e5e7eb)' }}>
          {brand?.logoUrl && <img src={brand.logoUrl} alt="" className="h-9 mx-auto mb-4 object-contain" />}
          <h1 className="text-lg font-semibold mb-2" style={{ color }}>
            {t('host_restricted_title', { defaultValue: 'Team access only', })}
          </h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
            {t('host_restricted_body', { defaultValue: 'This workspace is restricted to {{name}} team members.', name: brand?.name || 'this company' })}
          </p>
          <p className="text-xs text-neutral-500 mb-6">
            {t('host_restricted_signed_as', { defaultValue: "You're signed in as {{email}}, which isn't a member of this team.", email: user.email })}
          </p>
          <button onClick={() => signOut()} className="px-4 py-2 text-sm rounded text-white" style={{ background: color }}>
            {t('sign_out', { defaultValue: 'Sign out' })}
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen flex items-center justify-center"><span className="text-sm text-neutral-500">{children}</span></div>;
}
